<script setup lang="ts">
import { ref, computed } from 'vue'
import { TalentPoolForm, FormNavigation } from './talentPoolForm'
import { useTalentPoolsStore } from '@/stores/talentPoolsStore'
import { CircleX } from 'lucide-vue-next'
import { useProjectsStore } from '@/stores/projectsStore'
import type { TalentPoolFormExposed } from '@/types/talentPoolForm'

defineExpose({ show, close })

const open = ref(false)
const formRef = ref<TalentPoolFormExposed | null>(null)
const talentPoolsStore = useTalentPoolsStore()
const projectsStore = useProjectsStore()

const wizardMeta = computed(() => {
  const form = formRef.value
  if (!form) return { step: 1, total: 1, label: '' }
  const step = (form.currentStepIndex ?? 0) + 1
  const total = form.steps.length
  const label = form.currentStep.label
  return { step, total, label }
})

function show() {
  open.value = true
}

function close() {
  open.value = false
}

const handleUpdate = async () => {
  if (!formRef.value) return
  const { form, newProject, projectMode, selectedProject, editor } = formRef.value as any

  let projectId: number | null = null

  if (form.type === 'project') {
    if (projectMode === 'existing' && selectedProject) {
      projectId = selectedProject.id
    } else if (projectMode === 'new') {
      const project = await projectsStore.createProject(newProject)
      if (!project) {
        console.error('Project creation failed.')
        return
      }
      projectId = project.id
    }
  }

  const updatedPool = {
    type: form.type,
    location: form.location.address.trim(),
    location_lat: form.location.lat,
    location_lng: form.location.lng,
    title: form.title.trim(),
    description: editor.getJSON(),
    workType: form.workType,
    rate: form.salary,
    rateType: form.salaryType,
    hiring_timeline: form.hiringTimeline,
    timeline: form.hiringTimeline === 'specific_date' ? form.timeline : null,
    requirements: form.requirements,
    qualifications: form.qualifications,
    projectId,
  }

  // await talentPoolsStore.updateTalentPool(talentPoolsStore.activePool.id, updatedPool)
  close()
}
</script>

<template>
  <teleport to="body">
    <transition name="fade">
      <div v-if="open" class="fixed inset-0 z-50 flex items-center justify-center">
        <div class="absolute inset-0 bg-black/50" @click="close"></div>

        <div class="relative bg-white rounded-2xl max-w-3xl shadow-lg w-11/12 flex flex-col max-h-[90vh]">
          <div class="px-6 py-4 border-b border-gray-200">
            <div class="flex justify-between items-center">
              <h2 class="text-xl font-semibold text-gray-900 leading-tight">Edit Talent Pool</h2>
              <p class="text-sm text-gray-500">{{ wizardMeta.label }}</p>
              <div class="flex items-center gap-4">
                <span class="text-sm font-medium text-gray-500">
                  Step {{ wizardMeta.step }} of {{ wizardMeta.total }}
                </span>
                <CircleX class="h-6 w-6 text-gray-500 cursor-pointer" @click="close" />
              </div>
            </div>
          </div>

          <TalentPoolForm ref="formRef" mode="edit" :initial-data="talentPoolsStore.activePool" />

          <FormNavigation
            v-if="formRef"
            :can-proceed="formRef.canProceedToNextStep"
            :is-first-step="formRef.currentStepIndex === 0"
            :is-last-step="formRef.currentStepIndex === formRef.steps.length - 1"
            :submit-label="'Update Talent Pool'"
            @prev="formRef.decreaseStep()"
            @next="formRef.increaseStep()"
            @submit="handleUpdate"
            @cancel="close"
          />
        </div>
      </div>
    </transition>
  </teleport>
</template>
